/**
 * Customers list — Stage 1 deliverable.
 *
 * Reuses the existing `/api/users/` paginated endpoint via
 * `useCustomerList`. No new BI endpoint needed for v1.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { DataView } from '@/components/data/DataView';
import { useCustomerList, type CustomerRow } from '@/api/legacy/customers';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const ROLE_FILTERS: Array<{ key: string; label: string; role: string }> = [
  { key: 'all', label: 'All', role: '' },
  { key: 'customer', label: 'Customers', role: 'customer' },
  { key: 'staff', label: 'Staff', role: 'staff' },
  { key: 'support', label: 'Support', role: 'support' },
  { key: 'rider', label: 'Riders', role: 'rider' },
];

export function CustomersPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [roleKey, setRoleKey] = useState('customer');
  const [verifiedOnly, setVerifiedOnly] = useState<boolean | null>(null);

  const role = ROLE_FILTERS.find((r) => r.key === roleKey)?.role || '';
  const query = useCustomerList({
    search, role, isVerified: verifiedOnly, page, pageSize,
  });

  const columns = useMemo<ColumnDef<CustomerRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Customer',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {row.original.name || row.original.username || `User #${row.original.id}`}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {row.original.phone_number ?? row.original.contact ?? row.original.email ?? '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'verified',
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            {row.original.is_verified ? (
              <Badge tone="success">verified</Badge>
            ) : (
              <Badge tone="warning">unverified</Badge>
            )}
            {row.original.is_active === false ? <Badge tone="danger">inactive</Badge> : null}
            {row.original.role ? <Badge tone="neutral">{row.original.role}</Badge> : null}
          </div>
        ),
      },
      {
        id: 'orders',
        header: () => <div className="text-right">Orders</div>,
        cell: ({ row }) => {
          // react_api UserSerializer ships `orders_count`; legacy callers
          // (and the staff variant) use `total_orders`. Prefer either.
          const v = row.original.orders_count ?? row.original.total_orders;
          return (
            <div className="kpi-num text-right text-sm">
              {v != null ? fmt.number(v) : '—'}
            </div>
          );
        },
      },
      {
        id: 'last_active',
        header: 'Last active',
        cell: ({ row }) =>
          row.original.last_active ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.last_active), 'MMM d, yyyy')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
      {
        id: 'joined',
        header: 'Joined',
        cell: ({ row }) =>
          row.original.date_joined ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.date_joined), 'MMM d, yyyy')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
    ],
    [],
  );

  const total = query.data?.total_items ?? query.data?.count ?? (query.data?.results?.length ?? 0);
  const rows = query.data?.results ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Customers"
        title="Customer 360"
        description="Search any customer to open their history, cart and last-known location."
      />

      <div className="mt-4">
        <DataView<CustomerRow>
          title="People"
          data={rows}
          columns={columns}
          isLoading={query.isLoading}
          isError={query.isError}
          onRetry={() => void query.refetch()}
          search={{ value: search, onChange: (v) => { setSearch(v); setPage(1); }, placeholder: 'Search by name, phone, email…' }}
          pagination={{
            page, pageSize, total,
            onPageChange: setPage,
            onPageSizeChange: (n) => { setPageSize(n); setPage(1); },
          }}
          onRowClick={(r) => navigate(`/customers/${r.id}`)}
          emptyTitle="No people match these filters"
          toolbar={
            <div className="flex flex-wrap items-center gap-1.5">
              {ROLE_FILTERS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => { setRoleKey(r.key); setPage(1); }}
                  className={cn(
                    'press rounded-full border px-2.5 py-1 text-[11px] focus-ring',
                    roleKey === r.key
                      ? 'border-primary/30 bg-primary/10 text-primary'
                      : 'border-border bg-surface text-muted-foreground hover:text-foreground',
                  )}
                >
                  {r.label}
                </button>
              ))}
              <span className="mx-1 h-4 w-px bg-border" aria-hidden />
              <button
                type="button"
                onClick={() =>
                  setVerifiedOnly((v) => (v === null ? true : v === true ? false : null))
                }
                className={cn(
                  'press rounded-full border px-2.5 py-1 text-[11px] focus-ring',
                  verifiedOnly === null && 'border-border bg-surface text-muted-foreground hover:text-foreground',
                  verifiedOnly === true && 'border-success/40 bg-success/10 text-success',
                  verifiedOnly === false && 'border-warning/40 bg-warning/10 text-warning',
                )}
              >
                {verifiedOnly === null ? 'verification' : verifiedOnly ? 'verified' : 'unverified'}
              </button>
            </div>
          }
        />
      </div>
    </>
  );
}
